import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Link } from '@fluentui/react';
import { findSettingElement, getSettingRect, scrollSettingIntoView } from '../settings/dom';
import { revealAncestors, setPendingFocusId } from '../settings/reveal';
import { findSetting, ISettingDescriptor } from '../settings/registry';

/**
 * Body of the "couldn't find it" toast for a setting the registry knows about.
 *
 * Two sentences, never spliced together: the generic "couldn't find it" line, then - when the
 * registry has one - the setting's own `condition` as a second, standalone sentence ("Only appears
 * when Theme is set to Custom."). `dependsOn` adds a link straight to the setting that controls it,
 * so the answer to "why can't I find this" is one click from being visible instead of just explained.
 */
const SettingNotFoundToast: React.FC<{ setting: ISettingDescriptor; onViewDependency: (id: string) => void }> = ({
    setting,
    onViewDependency,
}) => (
    <div>
        <div>Couldn&apos;t find &quot;{setting.label}&quot; on this page.</div>
        <div>{setting.condition ?? 'It may have moved, or may not apply to this configuration.'}</div>
        {setting.dependsOn && (
            <Link onClick={() => onViewDependency(setting.dependsOn as string)} style={{ display: 'inline-block', marginTop: 4 }}>
                View that setting
            </Link>
        )}
    </div>
);

/*
 * Giving up is IDLE-based, not a wall clock.
 *
 * A fixed deadline cannot tell "this field is not on the page" from "this panel is still fetching".
 * The Product Cards panel builds its example from a real product, and on a cold load it crossed a
 * flat 10s: the field did arrive, but the toast had already claimed it was missing — the mechanism
 * lying about the one thing it exists to be honest about. Waiting until the DOM has been QUIET
 * instead means the toast only fires once the page has stopped working, which is the only moment
 * "it is not here" is actually true. The ceiling stops a page that never settles (polling widgets,
 * animation loops) from waiting forever.
 */
const QUIET_BEFORE_GIVING_UP_MS = 2500;
/*
 * Quiet alone is not enough: while a fetch is in flight the DOM is perfectly still, so an idle timer
 * expires mid-request and the toast lies again. Nothing may be reported missing before this floor.
 */
const MIN_WAIT_MS = 12000;
const MAX_WAIT_MS = 30000;
/** Polling backstop for renders that a MutationObserver does not see (e.g. a style-only reveal). */
const POLL_INTERVAL_MS = 250;
/** Time for the smooth scroll to settle before measuring. */
const SCROLL_SETTLE_MS = 400;
/** Time for an expanding accordion to finish laying out before scrolling to what it revealed. */
const REVEAL_SETTLE_MS = 250;

export interface ISettingFocusState {
    /** The setting being pointed at, when it is known to the registry. */
    setting?: ISettingDescriptor;
    /** The raw id from the URL, which may be an alias or an unknown id. */
    focusId?: string;
    element?: HTMLElement;
    rect?: DOMRect;
    dismiss: () => void;
}

/**
 * Resolves `?focus=<settingId>` on the current route into a live element and its rectangle.
 *
 * Waits for the field to appear rather than assuming it is already rendered, keeps the rectangle
 * current while the user scrolls, and reports honestly when the setting never turns up.
 */
export const useSettingFocus = (): ISettingFocusState => {
    const location = useLocation();
    const history = useHistory();
    const focusId = new URLSearchParams(location.search).get('focus') ?? undefined;

    const [element, setElement] = React.useState<HTMLElement | undefined>();
    const [rect, setRect] = React.useState<DOMRect | undefined>();

    /**
     * Clears the highlight and takes `focus` out of the URL.
     *
     * Deliberately `window.history.replaceState` rather than the router's `replace`: dismissing a
     * highlight must not look like a navigation. Routing it through the router re-rendered the
     * screen underneath, which closed any panel the highlighted setting was sitting in - clicking
     * "Got it" on a setting inside the Search Settings panel shut the panel. This rewrites the
     * address bar without telling React Router anything happened, so nothing under the callout
     * re-renders.
     *
     * Because the router's location does not change, the effect below does not re-run, so the
     * state cleared here stays cleared.
     */
    const dismiss = React.useCallback(() => {
        setElement(undefined);
        setRect(undefined);
        const params = new URLSearchParams(window.location.search);
        if (params.has('focus')) {
            params.delete('focus');
            const search = params.toString();
            window.history.replaceState(
                window.history.state,
                '',
                `${window.location.pathname}${search ? `?${search}` : ''}`
            );
        }
        /*
         * The replaceState above is invisible to the router (that is the whole point - see the
         * comment above), so anything reading "is a focus pending" through useLocation() would keep
         * seeing the dismissed id. Clearing the shared store directly is what lets
         * ConditionalSetting's ghost - and the accordion reveal - agree that this focus is over.
         */
        setPendingFocusId(undefined);
    }, []);

    /*
     * Keep the shared store in step with every REAL navigation (the resolver landing on a new
     * setting, a "View that setting" link, the back button) - the only other way `focusId` changes.
     * `dismiss()` above handles the one way it does not: clearing the URL without going through the
     * router.
     */
    React.useEffect(() => {
        setPendingFocusId(focusId);
    }, [focusId]);

    // Find the element, waiting for it to render.
    React.useEffect(() => {
        if (!focusId) {
            setElement(undefined);
            setRect(undefined);
            return;
        }

        let settled = false;
        let observer: MutationObserver | undefined;
        let poll: ReturnType<typeof setInterval> | undefined;
        let giveUp: ReturnType<typeof setTimeout> | undefined;
        let ceiling: ReturnType<typeof setTimeout> | undefined;
        let settleTimer: ReturnType<typeof setTimeout> | undefined;

        const stopWaiting = () => {
            settled = true;
            observer?.disconnect();
            if (poll) clearInterval(poll);
            if (giveUp) clearTimeout(giveUp);
            if (ceiling) clearTimeout(ceiling);
        };

        const attempt = () => {
            if (settled) return;
            const found = findSettingElement(focusId);
            if (!found) return;
            stopWaiting();
            setElement(found);
            /*
             * The setting may be inside a collapsed accordion, which renders its children hidden
             * while a focus is pending precisely so they can be found here. Ask every collapsible
             * ancestor to open before measuring: expanding changes layout, so a rectangle taken
             * first would point at the wrong place - or at nothing, since a hidden element has no box.
             */
            const revealDelay = revealAncestors(found) > 0 ? REVEAL_SETTLE_MS : 0;
            settleTimer = setTimeout(() => {
                /*
                 * Re-query rather than reusing `found`. The element located a moment ago may have
                 * been the hidden copy a collapsed accordion rendered so it could be found at all;
                 * expanding mounts the real children and unmounts that copy, and measuring the
                 * detached node returns zeros - which is a highlight that never appears.
                 */
                const target = findSettingElement(focusId) ?? found;
                setElement(target);
                scrollSettingIntoView(target);
                settleTimer = setTimeout(() => setRect(getSettingRect(target)), SCROLL_SETTLE_MS);
            }, revealDelay);
        };

        attempt();
        if (!settled) {
            const abandon = () => {
                if (settled) return;
                stopWaiting();
                const known = findSetting(focusId);
                toast.error(
                    known ? (
                        <SettingNotFoundToast
                            setting={known}
                            onViewDependency={(id) => history.push(`/go/setting/${encodeURIComponent(id)}`)}
                        />
                    ) : (
                        `Couldn't find the setting "${focusId}" on this page.`
                    )
                );
            };
            /*
             * Restarted by every mutation, so the countdown only runs while the page is quiet — and
             * never allowed to fire before MIN_WAIT_MS, because a page waiting on the network is
             * also perfectly quiet.
             */
            const startedAt = Date.now();
            const resetIdle = () => {
                if (giveUp) clearTimeout(giveUp);
                const remainingFloor = MIN_WAIT_MS - (Date.now() - startedAt);
                const delay = Math.max(QUIET_BEFORE_GIVING_UP_MS, remainingFloor);
                giveUp = setTimeout(abandon, delay);
            };
            resetIdle();
            ceiling = setTimeout(abandon, MAX_WAIT_MS);
            observer = new MutationObserver(() => {
                resetIdle();
                attempt();
            });
            observer.observe(document.body, { childList: true, subtree: true });
            poll = setInterval(attempt, POLL_INTERVAL_MS);
        }

        return () => {
            stopWaiting();
            if (settleTimer) clearTimeout(settleTimer);
        };
    }, [focusId, location.pathname]);

    // Keep the rectangle in step with scrolling and resizing while the highlight is up.
    React.useEffect(() => {
        if (!element) return;
        let frame = 0;
        const update = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setRect(getSettingRect(element)));
        };
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [element]);

    // Escape dismisses, matching every other transient surface in the app.
    React.useEffect(() => {
        if (!element) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') dismiss();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [element, dismiss]);

    return { setting: findSetting(focusId), focusId, element, rect, dismiss };
};

export default useSettingFocus;
